import React, { useEffect, useMemo, useState } from 'react';

import { UpliftIndex, UpliftIndexTypePascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import { NumberUtils } from '@/utils/numbers.utils';
import styles from '../uplift-rates.module.scss';
import RateActionsCellRenderer from './RateActionsCellRenderer';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';

interface TableTabProps {
  selectedIndex: UpliftIndex | null;
  isAdminOrFullAccessRole?: boolean;
  regionName?: string | null;
}

export const TableTab: React.FC<TableTabProps> = ({ selectedIndex, isAdminOrFullAccessRole = false, regionName }) => {
  const { searchUsers } = useSearchUsers();
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    const fetchUsers = async () => {
      const rates = selectedIndex?.upliftIndexRates?.filter((r) => !!r) || [];
      const ids: string[] = [];
      rates.forEach((r: any) => {
        if (r?.createdBy) ids.push(r.createdBy);
        if (r?.updatedBy) ids.push(r.updatedBy);
      });
      const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
      if (uniqueIds.length === 0) {
        setUsers([]);
        return;
      }
      const result = await searchUsers({ ids: uniqueIds as any });
      setUsers(result || []);
    };
    fetchUsers();
  }, [selectedIndex, searchUsers]);

  const getUserName = (id?: string | null) => {
    if(!id && selectedIndex?.type == UpliftIndexTypePascalCaseEnum.Custom) {
      return '';
    }
    if(!id && selectedIndex?.type == UpliftIndexTypePascalCaseEnum.Published) {
      return 'System';
    }
    const user = users.find((x) => x?.Id === id);
    return user ? `${user.FirstName} ${user.LastName}` : 'Unknown User';
  };
  const columnDefs = useMemo(() => [
    {
      headerName: 'Effective Date',
      field: 'effectiveDate',
      sortable: true,
      filter: 'agDateColumnFilter',
      width: 200,
      valueFormatter: (params: any) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      },
      sort: 'desc'
    },
    {
      headerName: 'Rate',
      field: 'rate',
      sortable: true,
      filter: 'agNumberColumnFilter',
      width: 150,
      valueFormatter: (params: any) => {
        if (!params.value) return '';
        // convert to percentage for display
        return NumberUtils.prettifyWithRound(parseFloat(params.value) * 100, 3);
      },
      cellStyle: {
        textAlign: 'right',
        fontWeight: '600',
        color: '#036d83'
      }
    },
    {
      headerName: 'Created By',
      field: 'createdByName',
      sortable: true,
      filter: 'agTextColumnFilter',
      width: 220,
    },
    {
      headerName: 'Updated By',
      field: 'updatedByName',
      sortable: true,
      filter: 'agTextColumnFilter',
      width: 220,
    },
    {
      headerName: '',
      field: 'actions',
      width: 320,
      sortable: false,
      filter: false,
      cellRenderer: RateActionsCellRenderer,
      cellRendererParams: {
        selectedIndex,
        isAdminOrFullAccessRole,
        regionName,
      },
    }
  ], [selectedIndex, isAdminOrFullAccessRole, regionName]);

  const rowData = useMemo(() => {
    if (!selectedIndex?.upliftIndexRates) {
      return [];
    }

    return selectedIndex.upliftIndexRates
      .filter(indexRate => indexRate != null)
      .map(indexRate => ({
        id: indexRate!.id,
        effectiveDate: indexRate!.effectiveDate,
        rate: indexRate!.rate,
        createdBy: indexRate!.createdBy,
        createdByName: getUserName(indexRate!.createdBy),
        updatedByName: getUserName(indexRate!.updatedBy),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, users]);

  if (!selectedIndex) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateContent}>
          <span className={styles.emptyStateIcon}>📋</span>
          <h3>Select an Index to View Table</h3>
          <p>Please select a region and index from the dropdowns above to view the rate data in table format.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tableTab}>
      <div className={styles.tableHeader}>
        <h3>📋 {selectedIndex.name}</h3>
        <div className={styles.indexInfo}>
          <span className={styles.dataPoints}>
            {rowData.length} records
          </span>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <TableGrid
          columnDefs={columnDefs as any}
          rowData={rowData}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: true
          }}
          pagination={true}
          paginationPageSize={10}
          domLayout="autoHeight"
        />
      </div>

    </div>
  );
};
